import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as Tick } from '../../assets/icons/tick-circle.svg';
import { SebiDisclosureKind, sebiDisclosureMessageFor } from '../../utils/sebiDisclosureConfig';

interface DisclosureBlockProps {
    title: string;
    points: string[];
    source?: string;
}

const DisclosureBlock: React.FC<DisclosureBlockProps> = ({ title, points, source }) => (
    <div className='discBlock'>
        <div className='discHeader'>{title}</div>
        {points.map((point) => (
            <div key={point} className='pointers-flex pt-2'>
                <Tick className='discTick' />
                <div className='discPointText'>{point}</div>
            </div>
        ))}
        {source && <div className='discSource'>{source}</div>}
    </div>
);

const Chevron: React.FC<{ direction: 'left' | 'right' }> = ({ direction }) => (
    <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
        <path
            d={direction === 'left' ? 'M10 3L5 8l5 5' : 'M6 3l5 5-5 5'}
            stroke='currentColor'
            strokeWidth='1.8'
            strokeLinecap='round'
            strokeLinejoin='round'
        />
    </svg>
);

interface RiskDisclosureProps {
    kind?: SebiDisclosureKind;
    onAcknowledge: () => void;
}

const ACK_LABELS = ['I understand', 'ठीक है'];

// English on page 1, Hindi on page 2; the footer button acknowledges from either page, labelled in that page's language.
const RiskDisclosure: React.FC<RiskDisclosureProps> = ({ kind = 'risk', onAcknowledge }) => {
    const message = sebiDisclosureMessageFor(kind);
    const pages: DisclosureBlockProps[] = [
        { title: message.titleEn, points: message.pointsEn, source: message.sourceEn },
        { title: message.titleHi, points: message.pointsHi, source: message.sourceHi },
    ];
    const [page, setPage] = useState(0);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [page]);

    return (
        <>
        <div className='discContent'>
            <div className='discScroll' ref={scrollRef} lang={page === 0 ? 'en' : 'hi'}>
                <DisclosureBlock {...pages[page]} />
            </div>
            <div className='discPager'>
                <button
                    type='button'
                    className='discPagerArrow'
                    aria-label='English'
                    disabled={page === 0}
                    onClick={() => setPage(0)}
                >
                    <Chevron direction='left' />
                </button>
                <div className='discPagerDots'>
                    {pages.map((_, index) => (
                        <span key={index} className={index === page ? 'discPagerDot active' : 'discPagerDot'} />
                    ))}
                </div>
                <button
                    type='button'
                    className='discPagerArrow'
                    aria-label='हिंदी'
                    disabled={page === pages.length - 1}
                    onClick={() => setPage(1)}
                >
                    <Chevron direction='right' />
                </button>
            </div>
        </div>
        <div className='discFooter'>
            <button autoFocus type='button' className='ackButton c-pointer' onClick={onAcknowledge}>
                {ACK_LABELS[page]}
            </button>
        </div>
        </>
    );
};

export default RiskDisclosure
